import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Clock, ChevronLeft, ChevronRight, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '../services/supabaseClient';
import { getStoredSession } from '../hooks/useAuth';

interface Question {
  question: string;
  options: string[];
  correct_index: number;
  difficulty: string;
  explanation: string;
}

interface AnswerKey {
  question_index: number;
  correct_index: number;
}

interface Assessment {
  id: string;
  title: string;
  subject: string | null;
  deadline: string;
  instructions: string | null;
  questions_json: Question[];
  answer_key: AnswerKey[];
}

export default function AssignmentTaking() {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const session = getStoredSession();
  const navigate = useNavigate();

  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [currentQ, setCurrentQ] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState<number | null>(null);

  const questionStartRef = useRef<number>(Date.now());
  const timePerQuestion = useRef<Record<number, number>>({});
  const deadlineRef = useRef<Date | null>(null);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);

  useEffect(() => {
    if (!assignmentId || !session?.student_id) return;

    supabase.rpc('get_student_assessment_detail', {
      p_assessment_id: assignmentId,
      p_student_id: session.student_id,
    }).then(({ data: rows, error }) => {
      if (error || !rows?.length) { navigate('/assignments'); return; }
      const row = rows[0];
      const a: Assessment = {
        id: row.id,
        title: row.title,
        subject: row.subject,
        deadline: row.deadline,
        instructions: row.instructions,
        questions_json: row.questions_json as Question[],
        answer_key: row.answer_key as AnswerKey[],
      };
      setAssessment(a);
      deadlineRef.current = new Date(a.deadline);

      if (row.submitted_at) {
        setAnswers((row.existing_answers as Record<number, number>) ?? {});
        setScore(row.existing_score);
        setSubmitted(true);
        setLoading(false);
        return;
      }

      if (row.existing_answers) {
        setAnswers(row.existing_answers as Record<number, number>);
      }

      if (new Date(a.deadline) < new Date()) {
        if (row.existing_answers) submitFinal(a, row.existing_answers as Record<number, number>, true);
        else navigate('/assignments');
        return;
      }

      setLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId, session?.student_id]);

  useEffect(() => {
    if (!deadlineRef.current) return;
    const tick = () => {
      const left = Math.max(0, Math.floor((deadlineRef.current!.getTime() - Date.now()) / 1000));
      setTimeLeft(left);
      if (left === 0 && assessment && !submitted) {
        submitFinal(assessment, answers, true);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessment, submitted]);

  const autoSave = useCallback(async (updatedAnswers: Record<number, number>) => {
    if (!assignmentId || !session?.student_id) return;
    await supabase.rpc('save_assessment_progress', {
      p_assessment_id:     assignmentId,
      p_student_id:        session.student_id,
      p_answers:           updatedAnswers,
      p_time_per_question: timePerQuestion.current,
    });
  }, [assignmentId, session?.student_id]);

  function selectAnswer(qIdx: number, optIdx: number) {
    const elapsed = Math.round((Date.now() - questionStartRef.current) / 1000);
    timePerQuestion.current[qIdx] = (timePerQuestion.current[qIdx] ?? 0) + elapsed;
    questionStartRef.current = Date.now();
    const updated = { ...answers, [qIdx]: optIdx };
    setAnswers(updated);
    autoSave(updated);
  }

  function goToQuestion(idx: number) {
    const elapsed = Math.round((Date.now() - questionStartRef.current) / 1000);
    timePerQuestion.current[currentQ] = (timePerQuestion.current[currentQ] ?? 0) + elapsed;
    questionStartRef.current = Date.now();
    setCurrentQ(idx);
  }

  async function submitFinal(a: Assessment, finalAnswers: Record<number, number>, autoSubmit = false) {
    if (!session?.student_id) return;
    setSubmitting(true);

    let correct = 0;
    a.answer_key.forEach(({ question_index, correct_index }) => {
      if (finalAnswers[question_index] === correct_index) correct++;
    });

    await supabase.rpc('submit_assessment_response', {
      p_assessment_id:     a.id,
      p_student_id:        session.student_id,
      p_answers:           finalAnswers,
      p_time_per_question: timePerQuestion.current,
      p_auto_submitted:    autoSubmit,
      p_score:             correct,
      p_total:             a.questions_json.length,
    });

    setScore(correct);
    setSubmitted(true);
    setSubmitting(false);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-6 h-6 border-2 border-coral-warm border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!assessment) return null;

  const questions = assessment.questions_json;
  const total = questions.length;
  const answered = Object.keys(answers).length;
  const formatTime = (s: number) =>
    `${Math.floor(s / 3600).toString().padStart(2, '0')}:${Math.floor((s % 3600) / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  if (submitted) {
    const pct = score !== null ? Math.round((score / total) * 100) : null;
    const answerKeyMap: Record<number, number> = {};
    assessment.answer_key.forEach(({ question_index, correct_index }) => {
      answerKeyMap[question_index] = correct_index;
    });
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{assessment.title}</h1>
          {pct !== null && (
            <>
              <p className={`text-5xl font-bold mt-6 mb-2 ${pct >= 70 ? 'text-green-600' : pct >= 40 ? 'text-amber-600' : 'text-red-500'}`}>
                {pct}%
              </p>
              <p className="text-gray-500">{score} / {total} correct</p>
            </>
          )}
        </div>

        <div className="space-y-3 mb-8">
          {questions.map((q, qi) => {
            const correctIdx = answerKeyMap[qi] ?? q.correct_index;
            const studentAns = answers[qi];
            const isCorrect = studentAns === correctIdx;
            const isUnanswered = studentAns === undefined;
            return (
              <div key={qi} className={`bg-white rounded-xl border p-4 ${isUnanswered ? 'border-gray-200' : isCorrect ? 'border-green-200' : 'border-red-200'}`}>
                <div className="flex items-start gap-2 mb-3">
                  <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    isUnanswered ? 'bg-gray-100 text-gray-400' :
                    isCorrect ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>{qi + 1}</span>
                  <p className="text-sm font-medium text-gray-900 leading-relaxed">{q.question}</p>
                </div>
                <div className="space-y-1.5 ml-8">
                  {q.options.map((opt, oi) => {
                    const isCorrectOpt = oi === correctIdx;
                    const isStudentPick = oi === studentAns;
                    let cls = 'border-gray-100 text-gray-600';
                    if (isCorrectOpt) cls = 'border-green-300 bg-green-50 text-green-900';
                    else if (isStudentPick) cls = 'border-red-300 bg-red-50 text-red-900';
                    return (
                      <div key={oi} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${cls}`}>
                        <span className="font-semibold text-xs w-4 flex-shrink-0">{String.fromCharCode(65 + oi)}.</span>
                        <span className="flex-1">{opt}</span>
                        {isCorrectOpt && <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />}
                        {isStudentPick && !isCorrectOpt && <XCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />}
                      </div>
                    );
                  })}
                </div>
                {q.explanation && (
                  <p className="ml-8 mt-2 text-xs text-gray-500 italic">{q.explanation}</p>
                )}
              </div>
            );
          })}
        </div>

        <div className="text-center">
          <button
            onClick={() => navigate('/assignments')}
            className="px-6 py-3 bg-coral-warm text-white rounded-xl font-semibold hover:bg-orange-600 transition-colors"
          >
            Back to Assignments
          </button>
        </div>
      </div>
    );
  }

  const q = questions[currentQ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-800 truncate max-w-[200px]">{assessment.title}</p>
            <p className="text-xs text-gray-500">{answered}/{total} answered</p>
          </div>
          {timeLeft !== null && (
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-mono font-bold text-sm ${
              timeLeft < 300 ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-gray-100 text-gray-700'
            }`}>
              <Clock className="w-4 h-4" />
              {formatTime(timeLeft)}
            </div>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-gray-200">
        <div className="h-1 bg-coral-warm transition-all" style={{ width: `${(answered / total) * 100}%` }} />
      </div>

      {/* Question area */}
      <div className="flex-1 max-w-2xl mx-auto w-full px-4 py-6">
        {assessment.instructions && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-xs text-amber-800">
            {assessment.instructions}
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-semibold text-gray-400 uppercase">Question {currentQ + 1} of {total}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              q.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
              q.difficulty === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
            }`}>{q.difficulty}</span>
          </div>
          <p className="text-base font-medium text-gray-900 mb-5 leading-relaxed">{q.question}</p>
          <div className="space-y-2">
            {q.options.map((opt, oi) => (
              <button
                key={oi}
                onClick={() => selectAnswer(currentQ, oi)}
                className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-all ${
                  answers[currentQ] === oi
                    ? 'border-coral-warm bg-orange-50 text-ocean-deep font-medium'
                    : 'border-gray-200 bg-white hover:border-coral-warm hover:bg-orange-50/30 text-gray-700'
                }`}
              >
                <span className="font-semibold mr-2 text-gray-400">{String.fromCharCode(65 + oi)}.</span>
                {opt}
              </button>
            ))}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => goToQuestion(Math.max(0, currentQ - 1))}
            disabled={currentQ === 0}
            className="flex items-center gap-1 px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:border-coral-warm disabled:opacity-40 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>
          {currentQ < total - 1 ? (
            <button
              onClick={() => goToQuestion(currentQ + 1)}
              className="flex items-center gap-1 px-4 py-2 rounded-lg bg-coral-warm hover:bg-orange-600 text-white text-sm font-medium transition-colors"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={() => submitFinal(assessment, answers)}
              disabled={submitting}
              className="px-6 py-2 rounded-lg bg-green-600 hover:bg-green-700 disabled:bg-gray-200 text-white text-sm font-semibold transition-colors"
            >
              {submitting ? 'Submitting…' : 'Submit Test'}
            </button>
          )}
        </div>

        {/* Question palette */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs font-semibold text-gray-400 uppercase mb-3">Jump to question</p>
          <div className="flex flex-wrap gap-2">
            {questions.map((_, qi) => (
              <button
                key={qi}
                onClick={() => goToQuestion(qi)}
                className={`w-8 h-8 rounded-lg text-xs font-bold transition-colors ${
                  qi === currentQ ? 'bg-coral-warm text-white' :
                  answers[qi] !== undefined ? 'bg-orange-100 text-coral-warm' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                {qi + 1}
              </button>
            ))}
          </div>
        </div>

        {answered === total && (
          <button
            onClick={() => submitFinal(assessment, answers)}
            disabled={submitting}
            className="w-full mt-4 py-4 bg-green-600 hover:bg-green-700 disabled:bg-gray-200 text-white font-bold rounded-xl transition-colors text-base"
          >
            {submitting ? 'Submitting…' : `Submit All ${total} Answers`}
          </button>
        )}
      </div>
    </div>
  );
}
